import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import api, { isPublicPath } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface TimezoneInfo {
  value: string;
  label: string;
  offset: string;
}

interface TimezoneContextType {
  timezone: string;
  timezones: TimezoneInfo[];
  loading: boolean;
  updateTimezone: (timezone: string) => Promise<void>;
  refreshTimezone: () => Promise<void>;
}

const TimezoneContext = createContext<TimezoneContextType | undefined>(undefined);

const DEFAULT_TIMEZONE = 'America/Sao_Paulo';
const TIMEZONE_STORAGE_KEY = 'user_timezone';

// Lista padrão completa de timezones brasileiros
const DEFAULT_TIMEZONES: TimezoneInfo[] = [
  { value: 'America/Sao_Paulo', label: 'São Paulo (UTC-3)', offset: '-03:00' },
  { value: 'America/Manaus', label: 'Manaus (UTC-4)', offset: '-04:00' },
  { value: 'America/Fortaleza', label: 'Fortaleza (UTC-3)', offset: '-03:00' },
  { value: 'America/Recife', label: 'Recife (UTC-3)', offset: '-03:00' },
  { value: 'America/Rio_Branco', label: 'Rio Branco (UTC-5)', offset: '-05:00' },
  { value: 'America/Cuiaba', label: 'Cuiabá (UTC-4)', offset: '-04:00' },
  { value: 'America/Campo_Grande', label: 'Campo Grande (UTC-4)', offset: '-04:00' },
  { value: 'America/Boa_Vista', label: 'Boa Vista (UTC-4)', offset: '-04:00' },
  { value: 'America/Belem', label: 'Belém (UTC-3)', offset: '-03:00' },
  { value: 'America/Maceio', label: 'Maceió (UTC-3)', offset: '-03:00' },
  { value: 'America/Aracaju', label: 'Aracaju (UTC-3)', offset: '-03:00' },
  { value: 'America/Salvador', label: 'Salvador (UTC-3)', offset: '-03:00' },
  { value: 'America/Natal', label: 'Natal (UTC-3)', offset: '-03:00' },
  { value: 'America/Porto_Velho', label: 'Porto Velho (UTC-4)', offset: '-04:00' },
];

export function TimezoneProvider({ children }: { children: ReactNode }) {
  // useAuth() deve ser chamado normalmente (hooks não podem ser condicionais)
  // Protegemos o acesso às propriedades caso o contexto retorne undefined
  const authContext = useAuth();
  const user = authContext?.user || null;
  const isAuthenticated = authContext?.isAuthenticated || false;

  const [timezone, setTimezone] = useState<string>(DEFAULT_TIMEZONE);
  const [timezones, setTimezones] = useState<TimezoneInfo[]>(DEFAULT_TIMEZONES);
  const [loading, setLoading] = useState(true);
  const [timezonesFetched, setTimezonesFetched] = useState(false);

  // Buscar lista de timezones disponíveis (apenas se autenticado e ainda não buscado)
  useEffect(() => {
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
    const isPublicPortalRoute = isPublicPath(currentPath);

    const fetchTimezones = async () => {
      // Nunca buscar se estiver na rota temporária de escolha-clínica ou se não estiver autenticado
      if (isPublicPortalRoute || !isAuthenticated || timezonesFetched) {
        if (!isAuthenticated || isPublicPortalRoute) {
          setLoading(false);
        }
        return;
      }

      try {
        const response = await api.get('/users/timezones');
        if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
          setTimezones(response.data);
          setTimezonesFetched(true);
        }
        // Se não retornar dados válidos, manter os valores padrão já inicializados
      } catch (error) {
        // Erro silencioso - já temos valores padrão inicializados
        if (import.meta.env.DEV) {
          console.warn('Erro ao buscar timezones da API, usando valores padrão:', error);
        }
        // timezones já está inicializado com DEFAULT_TIMEZONES, não precisa fazer nada
      } finally {
        // Garantir que loading seja false mesmo se houver erro
        setLoading(false);
      }
    };

    fetchTimezones();
  }, [isAuthenticated, timezonesFetched]);

  // Buscar timezone do usuário
  useEffect(() => {
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
    const isPublicPortalRoute = isPublicPath(currentPath);

    const fetchUserTimezone = async () => {
      if (isPublicPortalRoute || !isAuthenticated || !user) {
        // Se não autenticado ou rota temporária, usar timezone do localStorage ou navegador
        const savedTimezone = localStorage.getItem(TIMEZONE_STORAGE_KEY);
        if (savedTimezone) {
          setTimezone(savedTimezone);
        } else {
          // Tentar detectar timezone do navegador
          const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          setTimezone(browserTimezone || DEFAULT_TIMEZONE);
        }
        setLoading(false);
        return;
      }

      try {
        const response = await api.get('/users/me');
        const userTimezone = response?.data?.timezone;

        if (userTimezone) {
          setTimezone(userTimezone);
          localStorage.setItem(TIMEZONE_STORAGE_KEY, userTimezone);
        } else {
          // Se não tiver timezone configurado, usar do localStorage ou padrão
          const savedTimezone = localStorage.getItem(TIMEZONE_STORAGE_KEY);
          if (savedTimezone) {
            setTimezone(savedTimezone);
          } else {
            setTimezone(DEFAULT_TIMEZONE);
          }
        }
      } catch (error) {
        // Erro silencioso - usar fallback
        if (import.meta.env.DEV) {
          console.warn('Erro ao buscar timezone do usuário, usando fallback:', error);
        }
        // Fallback para localStorage ou padrão
        const savedTimezone = localStorage.getItem(TIMEZONE_STORAGE_KEY);
        setTimezone(savedTimezone || DEFAULT_TIMEZONE);
      } finally {
        setLoading(false);
      }
    };

    fetchUserTimezone();
  }, [isAuthenticated, user]);

  const updateTimezone = async (newTimezone: string) => {
    try {
      if (isAuthenticated && user) {
        // Atualizar no backend usando endpoint do próprio usuário
        try {
          await api.patch('/users/me/timezone', { timezone: newTimezone });
        } catch (error) {
          // Se falhar, apenas logar mas continuar atualizando localmente
          if (import.meta.env.DEV) {
            console.warn('Erro ao atualizar timezone no backend, atualizando apenas localmente:', error);
          }
        }
      }

      // Sempre atualizar localmente, mesmo se API falhar
      setTimezone(newTimezone);
      localStorage.setItem(TIMEZONE_STORAGE_KEY, newTimezone);
    } catch (error) {
      // Se houver erro crítico, apenas logar mas não quebrar
      if (import.meta.env.DEV) {
        console.error('Erro ao atualizar timezone:', error);
      }
      // Não lançar erro para não quebrar a aplicação
    }
  };

  const refreshTimezone = async () => {
    if (!isAuthenticated || !user) return;

    try {
      const response = await api.get('/users/me');
      const userTimezone = response?.data?.timezone;

      if (userTimezone) {
        setTimezone(userTimezone);
        localStorage.setItem(TIMEZONE_STORAGE_KEY, userTimezone);
      }
    } catch (error) {
      // Erro silencioso - não quebrar a aplicação
      if (import.meta.env.DEV) {
        console.warn('Erro ao atualizar timezone:', error);
      }
    }
  };

  return (
    <TimezoneContext.Provider
      value={{
        timezone,
        timezones,
        loading,
        updateTimezone,
        refreshTimezone,
      }}
    >
      {children}
    </TimezoneContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTimezone() {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
}

